import { useAuthorizeUser } from "@coworker/apprestructured/src/shared/hooks/useAuthorizeUser";
import { SimpleDefaultLoadingScreen } from "@coworker/apprestructured/src/shared/simple/SimpleLoading/SimpleDefaultLoadingScreen";
import { Error } from "@coworker/apprestructured/src/login/components/Error/Error";
import { ErrorType } from "@coworker/apprestructured/src/shared/constants/errorTypes";
import { ReactElement } from "react";
import { NewUserPage } from "@coworker/apprestructured/src/login/components/NewUserPage/NewUserPage";
import { UnauthorizedPage } from "@coworker/apprestructured/src/login/components/Unauthorized/UnauthorizedPage";
import { useUserHasFixaBasicPermissions } from "@coworker/apprestructured/src/login/hooks/useUserHasRoleOnPlatformOwner";
import useFlag, {
  FLAGS,
  useUserPermissionsQuery,
} from "@coworker/app/src/hooks/useFlag";

export const AuthorizedUserRoute = ({
  children,
}: {
  children: ReactElement;
}) => {
  const userState = useAuthorizeUser();

  // TO-DO: COAPP-12897 - remove the feature flag once deployed to all markets.
  // Wait for flags to load before evaluating — prevents a flash where the user
  // briefly accesses the app before the flag resolves to true.
  const { isLoading: isFlagLoading } = useUserPermissionsQuery();
  const enableAtkomstUnauthorizedPage = useFlag(
    FLAGS.ENABLE_ATKOMST_UNAUTHORIZED_PAGE
  );

  // Only runs the Atkomst JWT decode when the flag is ON.
  // When flag is OFF: enabled=false → query never fires → no unnecessary Atkomst calls.
  const {
    data: hasFixaBasicPermissions,
    isLoading: isFixaBasicPermissionsLoading,
  } = useUserHasFixaBasicPermissions(enableAtkomstUnauthorizedPage);

  if (
    userState.isLoading ||
    isFlagLoading ||
    (enableAtkomstUnauthorizedPage && isFixaBasicPermissionsLoading)
  ) {
    return <SimpleDefaultLoadingScreen />;
  }

  if (
    enableAtkomstUnauthorizedPage &&
    !hasFixaBasicPermissions &&
    !userState.isError
  ) {
    return <UnauthorizedPage />;
  }

  // Redirect to NewUserPage for users that are not fully provisioned yet
  if (userState.errorType === ErrorType.NOTFOUND) {
    return <NewUserPage />;
  }

  if (userState.isError || userState.errorType === ErrorType.FORBIDDEN) {
    return (
      <Error isAccessIssue={userState.errorType === ErrorType.FORBIDDEN} />
    );
  }

  return userState?.isActive ? children : <Error isAccessIssue={false} />;
};
